import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingScreen = ({ isLoading }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="fixed inset-0 z-[9999] bg-gray-50 dark:bg-gray-950"
        >
          {/* Top Progress Bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-800 overflow-hidden">
            <motion.div
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{
                duration: 2.8,
                ease: "easeInOut",
              }}
              className="h-full bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-500"
            />
          </div>

          {/* Animated Background Orbs - Same as Homepage */}
          <div className="fixed inset-0 pointer-events-none overflow-hidden">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="absolute w-[200px] h-[200px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] rounded-full bg-gradient-to-r from-blue-400/10 to-cyan-400/10 dark:from-purple-400/20 dark:to-pink-400/20 blur-2xl md:blur-3xl"
              style={{
                top: '10%',
                left: '5%',
                transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
              className="absolute w-[200px] h-[200px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] rounded-full bg-gradient-to-r from-purple-400/10 to-pink-400/10 dark:from-blue-400/20 dark:to-cyan-400/20 blur-2xl md:blur-3xl"
              style={{
                bottom: '10%',
                right: '5%',
                transform: `translate(${mousePosition.x * -0.02}px, ${mousePosition.y * -0.02}px)`
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              className="absolute w-[180px] h-[180px] md:w-[350px] md:h-[350px] lg:w-[500px] lg:h-[500px] rounded-full bg-gradient-to-r from-teal-400/8 to-emerald-400/8 dark:from-amber-400/20 dark:to-orange-400/20 blur-2xl md:blur-3xl"
              style={{
                top: '50%',
                left: '50%',
                transform: `translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
              className="absolute w-[150px] h-[150px] md:w-[300px] md:h-[300px] lg:w-[400px] lg:h-[400px] rounded-full bg-gradient-to-r from-rose-400/8 to-orange-400/8 dark:from-teal-400/15 dark:to-emerald-400/15 blur-2xl md:blur-3xl"
              style={{
                top: '30%',
                right: '20%',
                transform: `translate(${mousePosition.x * -0.015}px, ${mousePosition.y * 0.015}px)`
              }}
            />
          </div>

          {/* Center Content */}
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="flex flex-col items-start gap-3 max-w-2xl"
            >
              {/* Word with Kanji */}
              <div className="flex flex-col gap-1">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">
                  komorebi
                </h2>
                <div className="text-2xl md:text-3xl text-gray-600 dark:text-gray-400">
                  木漏れ日
                </div>
              </div>

              {/* Pronunciation */}
              <div className="text-sm text-gray-500 dark:text-gray-500 italic">
                /koh-moh-reh-bee/
              </div>

              {/* Definition */}
              <div className="text-base md:text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                <span className="italic">noun</span> • Japanese
                <br />
                The sunlight that filters through the leaves of trees, creating a play of light and shadow.
              </div>

              {/* Animated dots */}
              <div className="flex gap-2 mt-2">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    animate={{
                      scale: [1, 1.5, 1],
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: i * 0.2,
                      ease: "easeInOut",
                    }}
                    className="w-2.5 h-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
